import {Avatar, Box} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {CustomMdChip, CustomMdChipComplete, CustomUserChipStyle} from './Styles';
import {IconCloseChip, PersonChip} from './buttons/IconButtons';
import {POSITION_MODEL} from "../Helpers/PositionModel";
import React from "react";
import CustomTooltip from './CustomTooltip';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * CustomMediumChip Component
 *
 * Renders a medium-sized chip or card element with a title, optional avatar or icon,
 * optional close action, optional right-side content, and additional child content.
 * It supports full-width rendering and visual disabling.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.title - The main label or title displayed on the chip.
 * @param {Function} [props.onClickClose] - Optional callback triggered when the close icon is clicked.
 * @param {Function} [props.onClick] - Optional callback triggered when the chip container is clicked.
 * @param {JSX.Element|null} [props.children=null] - Optional extra content rendered below the main row.
 * @param {boolean} [props.completeWidth=false] - If true, chip stretches to full container width.
 * @param {boolean} [props.showAvatar=false] - If true, displays an avatar or icon beside the title.
 * @param {string} [props.iconPosition=POSITION_MODEL.RIGHT] - Position of the avatar or icon.
 * @param {string|null} [props.avatarSrc=null] - Optional avatar image URL. If not set, fallback icon is shown.
 * @param {React.ElementType} [props.Icon=PersonChip] - Icon component shown if `avatarSrc` is not defined.
 * @param {JSX.Element|null} [props.rightContent=null] - Optional content (e.g., status badge or icon) shown at the end.
 * @param {boolean} [props.disabled=false] - If true, visually disables the chip (e.g., with lower opacity).
 * @returns {JSX.Element} The rendered chip component.
 */

const CustomMediumChip = ({
                              title,
                              onClickClose,
                              onClick,
                              children = null,
                              completeWidth = false,
                              showAvatar = false,
                              iconPosition = POSITION_MODEL.RIGHT,
                              avatarSrc = null,
                              Icon = PersonChip,
                              rightContent = null,
                              disabled=false,
                          }) => {

    const showClose = typeof onClickClose === 'function' && !disabled;

    const CustomMdChipCompleteStyle =  CustomMdChipComplete(disabled)
    const CustomMdChipStyle =  CustomMdChip(disabled)

    /**
     * Renders either an avatar (from `avatarSrc`) or the fallback icon.
     * @private
     */
    const renderIcon = () => {
        return <>
            {showAvatar &&
                (avatarSrc ? (
                    <Avatar alt={title} src={avatarSrc} className='flex-shrink-0' sx={CustomUserChipStyle.avatar}/>
                ) : (
                    <Avatar className='flex-shrink-0' sx={CustomUserChipStyle.avatar}>
                        <Icon style={CustomMdChipStyle.icon}/>
                    </Avatar>
                ))
            }
        </>
    }

    return (
        <Box  sx={completeWidth ? CustomMdChipCompleteStyle : CustomMdChipStyle} onClick={onClick}>
            <div className={`d-flex justify-content-between align-items-center w-100
                ${children && ' border-bottom-shades-300'}
                ${!showClose && ' pb-1'}` }
            >
                <div className={"justify-content-start d-flex gap-2 align-items-center flex-grow-1 minw-0"}>
                    {iconPosition === POSITION_MODEL.LEFT && renderIcon()}
                    <Box
                        component='p'
                        className={'p_cmc_title minw-0'}
                        sx={{ ...CustomMdChipCompleteStyle.p, whiteSpace: 'normal' }}
                    >
                        <CustomTooltip
                            text={title}
                            classes={'fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row'}
                            showIfTruncated={true}
                            placement={'top'}
                        />
                    </Box>
                    {iconPosition === POSITION_MODEL.RIGHT && renderIcon()}
                </div>

                { (showClose) && (
                    <IconButton
                        className='flex-shrink-0'
                        size='small'
                        onClick={(e) => {
                            e.stopPropagation();
                            onClickClose();
                        }}
                    >
                        <IconCloseChip className='icon-color-shades-400'/>
                    </IconButton>
                )}

                { rightContent &&
                    rightContent
                }
            </div>
            { children &&
                children
            }
        </Box>
    );
};

export default CustomMediumChip;
